import React, { useEffect, useState } from "react";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    FormGroup,
    Label,
    Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { formatDataBeforeSend } from "helpers/GeneralHelper";
import { updateRecruitment } from "model/AboutUsModel"; // ⬅️ replace with your actual API
import { useUserContext } from "libs/context/UserContext";
import moment from "moment";
import { updateFetchDetails } from "model/Partners";
import { requestACallBack, requestACancelCallBack } from "model/CandidateDetailsModel";


const CandidateCallbackModal = ({
    setRequestCallBackModalOpen,
    requestCallBackModalOpen,
}) => {
    const inputFields = {
        FullName: "",
        CandidatePhone: "",
        CallBackDate: "",
        CallBackTime: "",
        Notes: "",
        // TimeZone: 'A/K'
    };
    const formatOptions = [];

    const [fields, setFields] = useState(inputFields);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [submitLoading, setSubmitLoading] = useState(false);

    const closeModal = () => setRequestCallBackModalOpen(false);

    const { candidateDetailsData, fetchCandidateDetailsData } = useUserContext() || {};


    // ✅ Yup validation schema
    const validationSchema = Yup.object().shape({
        FullName: Yup.string().required("Full Name is required"),
        CandidatePhone: Yup.string()
            .matches(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, "Invalid phone number")
            .required("Phone number is required"),
        CallBackDate: Yup.string().required("Date is required"),
        CallBackTime: Yup.string().required("Time is required"),
        Notes: Yup.string().required("Notes are required"),
    });

    // Handle input change + validate field
    const handleChange = async (e) => {
        const { name, value } = e.target;
        setFields({ ...fields, [name]: value });

        try {
            await Yup.reach(validationSchema, name).validate(value);
            setErrors((prev) => ({ ...prev, [name]: "" }));
        } catch (err) {
            setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);

        try {
            await validationSchema.validate(fields, { abortEarly: false });
            setErrors({});

            const formData = formatDataBeforeSend(fields, inputFields, formatOptions);
            let response = await requestACallBack(formData); // ⬅️ replace API call if needed

            if (response?.IsSuccess) {
                toast.success(response?.Message || "Callback request submitted!");
                setFields(inputFields);
                fetchCandidateDetailsData()
                setSubmitLoading(false);
                closeModal();
                setFields(inputFields);
                scrollToTop()
            } else {
                toast.error("Failed to submit callback request");
            }
        } catch (err) {
            if (err.inner) {
                const formErrors = {};
                err.inner.forEach((e) => {
                    formErrors[e.path] = e.message;
                });
                setErrors(formErrors);
            } else {
                // toast.error("Something went wrong!");
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    const cancelCallback = async (e, Id) => {
        e.preventDefault();
        if (Id) {
            setSubmitLoading(true);
            try {
                let response = await requestACancelCallBack({ Id: Id }); // ⬅️ replace API call if needed
                if (response?.IsSuccess) {
                    toast.success(response?.Message);
                    fetchCandidateDetailsData()
                    setSubmitLoading(false);
                    closeModal();
                    setFields(inputFields);
                    scrollToTop()
                } else {
                    toast.error("Failed to submit callback request");
                }
            } catch (err) {
                // toast.error("Something went wrong!");
            }
        }
    }


    // ✅ Prefill from candidateDetailsData
    useEffect(() => {
        if (candidateDetailsData) {
            const rawDate = candidateDetailsData?.CallBack?.CallBackDateAppFormat;
            const rawTime = candidateDetailsData?.CallBack?.CallBackTime;


            setFields((prev) => ({
                ...prev,
                FullName: `${candidateDetailsData?.CallBack?.FullName || `${candidateDetailsData?.CandidateFName} ${candidateDetailsData?.CandidateLName}`}`,
                CandidatePhone: candidateDetailsData?.CallBack?.CandidatePhone || candidateDetailsData?.CandidatePhone?.replace(/\D+/g, ""),
                Notes: candidateDetailsData?.CallBack?.Notes || "",
                CallBackDate: rawDate ? moment(rawDate).format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
                CallBackTime: rawTime || "",
            }));
        }
    }, [candidateDetailsData]);

    const scrollToTop = () => {
        window.scrollTo(0);
    };


    return (
        <Modal
            isOpen={requestCallBackModalOpen}
            toggle={closeModal}
            backdrop="static"
            className="modal-dialog-left modal-full-height"
        >
            <ModalBody className="p-4 d-flex flex-column"
                //   style={{ height: "100vh" }}
                style={{ maxHeight: "95vh", overflowY: "auto" }}
            >
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Call Back Request</h5>
                    <Button close onClick={closeModal} />
                </div>
                {candidateDetailsData?.CallBack?.FullName &&
                    <div className="message-box">
                        Callback request already sent.
                    </div>
                }

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-grow-1 d-flex flex-column mt-2">
                    <div className="flex-grow-1 overflow-auto pe-2">
                        {/* Full Name */}
                        <FormGroup>
                            <Label>Full Name *</Label>
                            <Input
                                type="text"
                                name="FullName"
                                placeholder="Enter full name"
                                value={fields.FullName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={candidateDetailsData?.CallBack?.FullName}
                            />
                            {(touched.FullName || errors.FullName) && (
                                <div className="text-danger small">{errors.FullName}</div>
                            )}
                        </FormGroup>

                        {/* Phone */}
                        <FormGroup>
                            <Label>Phone number *</Label>
                            <Input
                                type="text"
                                name="CandidatePhone"
                                placeholder="Enter Phone"
                                value={fields.CandidatePhone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={candidateDetailsData?.CallBack?.FullName}
                            />
                            {(touched.CandidatePhone || errors.CandidatePhone) && (
                                <div className="text-danger small">{errors.CandidatePhone}</div>
                            )}
                        </FormGroup>

                        {/* Date */}
                        <FormGroup>
                            <Label>Date *</Label>
                            <Input
                                type="date"
                                name="CallBackDate"
                                value={fields.CallBackDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={candidateDetailsData?.CallBack?.FullName}
                            />
                            {(touched.CallBackDate || errors.CallBackDate) && (
                                <div className="text-danger small">{errors.CallBackDate}</div>
                            )}
                        </FormGroup>

                        {/* Time */}
                        <FormGroup>
                            <Label>Time *</Label>
                            <Input
                                type="time"
                                name="CallBackTime"
                                value={fields.CallBackTime}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={candidateDetailsData?.CallBack?.FullName}
                            />
                            {(touched.CallBackTime || errors.CallBackTime) && (
                                <div className="text-danger small">{errors.CallBackTime}</div>
                            )}
                        </FormGroup>

                        {/* Notes */}
                        <FormGroup>
                            <Label>Notes *</Label>
                            <Input
                                type="textarea"
                                name="Notes"
                                placeholder="Enter notes"
                                value={fields.Notes}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={candidateDetailsData?.CallBack?.FullName}
                            />
                            {(touched.Notes || errors.Notes) && (
                                <div className="text-danger small">{errors.Notes}</div>
                            )}
                        </FormGroup>
                    </div>


                    {/* Buttons */}
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button color="secondary" onClick={closeModal} type="button" onClickCapture={(e) => cancelCallback(e, candidateDetailsData?.CallBack?._CandidateCallBackRequestId)}>
                            Cancel Request
                        </Button>
                        {!candidateDetailsData?.CallBack &&
                            <Button color="dark" type={!candidateDetailsData?.CallBack ? 'submit' : 'button'} disabled={submitLoading}>
                                {submitLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Submitting...
                                    </>
                                ) : (
                                    "Submit Request"
                                )}
                            </Button>
                        }
                    </div>
                </form>
            </ModalBody>
        </Modal>
    );
};

export default CandidateCallbackModal;
